import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconButton, InputBase, styled } from "@mui/material";
import { commonBoxShadowStyle } from "fe-common/ui/boxShadow";
import SearchIcon from "@mui/icons-material/Search";
import Fuse from "fuse.js";
import AddIcon from "@mui/icons-material/Add";
import { AddTableDataDialog } from "./genericDataTable/addTableDataDialog";
import { downloadObjectAsJson } from "fe-web-components/settings/util";
import { SaveOutlined } from "@mui/icons-material";
import { scrollableStyle } from "fe-common/ui/commonStyles";
import { PatientTableRow, TitleCell } from "./genericDataTable/patientTableRow";
import {PatientInfoReportData} from "./patientVisitData";

//region [[ Styles ]]

const PatientViewTableView = styled("div")({
  direction: "rtl",
  width: "100%",
  minWidth: "400px",
  ...scrollableStyle,
  flex: 1,
});

const TopTableBar = styled("div")({
  width: "100%",
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
});

const TableRow = styled("div")({
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  position: "relative",
});

const TableCell = styled("div")({
  padding: "15px",
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "15px",
  margin: "3px",
  backgroundColor: "aliceblue",
  width: "calc(100% / 9)",
  overflow: "hidden",
  fontSize: "10px",
});

const Search = styled(InputBase)({
  background: "white",
  borderRadius: "14px",
  // border: "1px solid #232323",
  padding: "5px",
  ...commonBoxShadowStyle,
  marginLeft: "15px",
  "& input": {
    padding: "0 2px",
    width: "90px",
    transition: "0.3s ease-out",
  },
  "& input:focus, &.active input": {
    width: "300px",
  },
  margin: "6px",
});

const Icon = styled(SearchIcon)({
  fill: "#9E9E9E",
});

const ImageButton = styled(IconButton)({});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientViewTableProps {
  data: Record<string, any>[];
  titles?: string[];
  onAddNewData?: (newData: PatientInfoReportData) => void;
  objectDescription: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

export const PatientViewTable = ({
  data,
  objectDescription,
  ...props
}: PatientViewTableProps) => {
  const fuseRef = useRef<Fuse<Record<string, any>>>(null);

  const [showAddNewData, setShowAddNewData] = useState(false);

  const [itemToEdit, setItemToEdit] = useState(null);

  useEffect(() => {
    fuseRef.current = new Fuse(data || [], {
      keys: Object.keys(data?.[0] || {}),
      threshold: 0.4,
      includeMatches: true,
    });
  }, [data]);

  const [searchTerm, setSearchTerm] = useState(null);

  const handleSearchTermChanged = (e) => {
    if (e?.target?.value === "") setSearchTerm(null);
    else setSearchTerm(e?.target?.value);
  };

  const filteredData = useMemo(
    () =>
      !searchTerm
        ? data.map((item) => ({ item, matches: [] }))
        : fuseRef.current.search?.(searchTerm),
    [searchTerm, data],
  );

  const handleItemToEdit = useCallback((item, index) => {
    setItemToEdit(item);
    setShowAddNewData(true);
  }, []);

  const schema = useMemo(() => data?.[0] || {}, [data]);

  return (
    <PatientViewTableView role="table">
      <TopTableBar>
        <Search
          placeholder={"סנן"}
          onChange={handleSearchTermChanged}
          value={searchTerm}
          startAdornment={<Icon />}
        />
        {!!props.onAddNewData && (
          <>
            <ImageButton onClick={() => setShowAddNewData(true)}>
              <AddIcon />
            </ImageButton>
            <AddTableDataDialog
              schema={schema}
              onSubmit={props.onAddNewData}
              open={showAddNewData}
              onClose={() => setShowAddNewData(false)}
              itemToEdit={itemToEdit}
            />
          </>
        )}
        <ImageButton
          onClick={() =>
            downloadObjectAsJson(
              JSON.stringify({ data: data ?? {}, objectDescription }),
              objectDescription,
            )
          }
        >
          <SaveOutlined />
        </ImageButton>
      </TopTableBar>
      <TableRow role="rowgroup">
        <TitleCell>#</TitleCell>
        {!props.titles?.length &&
          Object.keys(data?.[0] || {})?.map?.((key, index) =>
            key === "index" ? null : (
              <TitleCell key={"key" + index}>{key}</TitleCell>
            ),
          )}
        {!!props.titles?.length &&
          props.titles?.map?.((key, index) => (
            <TitleCell key={"key" + index}>{key}</TitleCell>
          ))}
      </TableRow>
      {filteredData?.map?.(({ item, matches }: {item:any , matches?: any}, index) => (
        <PatientTableRow
          key={item?.["id"] || index}
          item={item}
          index={index}
          matches={matches}
          onEditItem={handleItemToEdit}
        />
      ))}
    </PatientViewTableView>
  );
};
